import { useEffect, useReducer } from 'react';

import {
  Verb,
  WILDCARD_BUILD_URN,
  WILDCARD_TEST_URN,
  getMatrixHost,
  getResourcePermissions
} from '../utils/rbacPermissions';

// Module-scoped cache of permission lists, keyed by `${userId}|${urn}`.
// Values: array of lowercase level strings (e.g. ["read","write"]) or null
// (matrix unreachable -- treat as allow / fall-open).
const permissions = new Map();
const inflight = new Map();
const subscribers = new Set();

function cacheKey(userId, urn) {
  return `${userId || ''}|${urn || ''}`;
}

function notify() {
  for (const fn of subscribers) {
    fn();
  }
}

// Lets app-level code (e.g. logout) flush stale grants so a re-auth as a
// different user/role doesn't inherit the previous session's cache.
export function resetRbacCache() {
  permissions.clear();
  inflight.clear();
}

// useRbacPermissions returns `{ build, test, can, flagOn }`.
//
//   build: { canRead, canCreate, canUpdate, canDelete, canExecute }
//     pre-resolved booleans for the wildcard URN `automation:build:*`.
//
//   test:  { canRead, canCreate, canUpdate, canDelete, canExecute }
//     pre-resolved booleans for `automation:build:*:test_case_instance:*`.
//
//   can(verb, urn): escape hatch for per-resource gating (rarely needed today
//     because matrix grants live at the wildcard scope; reach for this only
//     when a specific buildId / testId needs its own permission check).
//
//   flagOn: boolean -- whether `is_rbac_enabled_cloud` is on for this user.
//     When false (or matrix host / auth / userId missing) `can` falls open
//     (`true`) and the build/test namespaces all read `true` too -- legacy
//     behaviour preserved.
//
// Auth is passed in by the caller so the hook stays host-agnostic (ml reads
// the token from Redux, mwc reads it from cookies, etc).
//
// `can` is synchronous: returns cached decision when known, otherwise kicks
// off an async `get-resource-permission` fetch and returns `false` (denied)
// for the in-flight window so the kebab doesn't render with items that
// vanish once matrix resolves. One network call per unique URN -- all verbs
// for that URN resolve from the same cached permission list.
export default function useRbacPermissions({ userId, authHeader, flagOn }) {
  // Local `forceRender` (useReducer) instead of dispatching a Redux action:
  // re-rendering only the components that called the hook is faster than a
  // global dispatch and avoids polluting the action log with internal cache
  // invalidations.
  const [, forceRender] = useReducer(prev => prev + 1, 0);
  // forceRender is the stable useReducer dispatch, so subscribe / unsubscribe
  // happen exactly once per mount. A matrix resolve that completes during the
  // initial render is still safe because getResourcePermissions is async --
  // its `.then` fires after this effect has registered the subscriber.
  useEffect(() => {
    subscribers.add(forceRender);
    return () => {
      subscribers.delete(forceRender);
    };
  }, [forceRender]);

  const can = (verb, urn) => {
    if (!flagOn || !getMatrixHost() || !userId || !authHeader || !verb || !urn) {
      return true;
    }
    const key = cacheKey(userId, urn);
    const levelKey = String(verb).toLowerCase();

    if (permissions.has(key)) {
      const levels = permissions.get(key);
      // null cache means matrix was unreachable -- fall open.
      return levels === null ? true : levels.includes(levelKey);
    }

    if (!inflight.has(key)) {
      const handleResolvedLevels = levels => {
        permissions.set(key, levels); // null -> treat as allow on next read
        inflight.delete(key);
        notify();
        return levels;
      };
      const pending = getResourcePermissions(userId, urn, authHeader).then(
        handleResolvedLevels
      );
      inflight.set(key, pending);
    }
    // In-flight: stay denied so we don't render a kebab that disappears
    // once matrix resolves. Fall-open paths above still return true.
    return false;
  };

  const build = {
    canRead:    can(Verb.READ,    WILDCARD_BUILD_URN),
    canCreate:  can(Verb.CREATE,  WILDCARD_BUILD_URN),
    canUpdate:  can(Verb.UPDATE,  WILDCARD_BUILD_URN),
    canDelete:  can(Verb.DELETE,  WILDCARD_BUILD_URN),
    canExecute: can(Verb.EXECUTE, WILDCARD_BUILD_URN)
  };
  const test = {
    canRead:    can(Verb.READ,    WILDCARD_TEST_URN),
    canCreate:  can(Verb.CREATE,  WILDCARD_TEST_URN),
    canUpdate:  can(Verb.UPDATE,  WILDCARD_TEST_URN),
    canDelete:  can(Verb.DELETE,  WILDCARD_TEST_URN),
    canExecute: can(Verb.EXECUTE, WILDCARD_TEST_URN)
  };

  return { build, test, can, flagOn: !!flagOn };
}
